import { Button } from '@material-ui/core';
import React, { useState, useEffect, useCallback } from 'react';
import firebase from "firebase/app";
import { useHistory } from 'react-router-dom';
import "./SystemCheck.css";
import icon from "./icon.png";
import logo from './../rcrm_logo.png';
import DetectRTC from 'detectrtc';
import swal from 'sweetalert';
import { getViewportGap, findPageOverlays, SIDE_PANEL_MIN_SIZE } from './ExamSecurity';
require('dotenv').config();

const SystemCheck = () => {
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buttonViewDisabled, setButtonViewDisabled] = useState(true);
  const [networkSpeed, setNetworkSpeed] = useState(null);
  const [webcamPermission, setWebcamPermission] = useState(null);
  const [browserValid, setBrowserValid] = useState(null);
  const [showWebcamButton, setShowWebcamButton] = useState(true); // Show "Activate Webcam" button if webcam not available
  const [webcamAvailable, setWebcamAvailable] = useState(false); // Track actual webcam availability from DetectRTC
  const [viewportClear, setViewportClear] = useState(true); // No side panel / split screen stealing the viewport
  const [viewportOverlays, setViewportOverlays] = useState([]); // Extension panels injected into the page

  // Disable right-click context menu
  useEffect(() => {
  if (document.addEventListener) {
      const handleContextMenu = (e) => {
      e.preventDefault();
      };
      document.addEventListener('contextmenu', handleContextMenu, false);
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu, false);
      };
    }
  }, []);

  // Check if internet connection is active
  async function checkInternetConnection() {
    try {
      // Use navigator.onLine as first check (fast but not always reliable)
      if (!navigator.onLine) {
        console.warn('⚠️ Navigator reports offline');
        sessionStorage.setItem("netspeed", "offline");
        return false;
      }

      // Try a lightweight fetch to verify actual connectivity
      // Using a small file with cache-busting to ensure fresh request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      try {
        const response = await fetch('https://www.google.com/favicon.ico?t=' + Date.now(), {
          method: 'HEAD',
          mode: 'no-cors',
          signal: controller.signal,
          cache: 'no-cache'
        });
        clearTimeout(timeoutId);
        console.log('✅ Internet connection is active');
        sessionStorage.setItem("netspeed", "connected");
        return true;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        // Try alternative endpoint
        try {
          const altResponse = await fetch('https://eu.httpbin.org/get?t=' + Date.now(), {
            method: 'GET',
            signal: controller.signal,
            cache: 'no-cache'
          });
          if (altResponse.ok) {
            console.log('✅ Internet connection is active (alternative check)');
            sessionStorage.setItem("netspeed", "connected");
            return true;
          }
        } catch (altError) {
          console.error('❌ Internet connection check failed:', altError);
        }
        sessionStorage.setItem("netspeed", "disconnected");
        return false;
      }
    } catch (error) {
      console.error('❌ Internet connection check failed:', error);
      sessionStorage.setItem("netspeed", "disconnected");
      return false;
    }
  }

  // Validate all system requirements
  const validateSystemRequirements = useCallback(async () => {
  let isAllowed = false;
    let networkOk = false;
    let browserOk = false;
    let webcamOk = false;

    // Check for a docked side panel / split screen stealing part of the window. Done here,
    // before the assessment starts, so the candidate can just close it - during the exam the
    // same condition costs them a security warning.
    const gap = getViewportGap();
    // Panels that shrink the window show up as a gap; panels injected into the page by an
    // extension steal no width at all and are only visible as a foreign node in the DOM
    const overlays = findPageOverlays();
    const viewportOk = gap < SIDE_PANEL_MIN_SIZE && overlays.length === 0;
    setViewportOverlays(overlays);
    setViewportClear(viewportOk);
    console.log(`🖥️ Screen check: ${viewportOk ? 'clear' : 'OBSTRUCTED'} - window gap ${gap}px (inner ${window.innerWidth}, outer ${window.outerWidth}), overlays: ${overlays.join(', ') || 'none'}`);

    // Check internet connection (just check if online, not speed)
    const netStatus = sessionStorage.getItem("netspeed");
    console.log('🌐 Internet connection check:', netStatus);
    if (netStatus === "connected" || netStatus === "online") {
      networkOk = true;
    isAllowed = true;
      setNetworkSpeed("Connected");
    } else {
      console.warn('⚠️ Internet connection is not available');
      setNetworkSpeed("Disconnected");
  }

    // Check browser version
  const browserConditions = {
    Chrome: 80,
    Firefox: 60,
    Safari: 12,
    Opera: 60,
    Edge: 80,
  };

    let browserValidated = false;
  for (const [browser, version] of Object.entries(browserConditions)) {
      if (DetectRTC.browser[`is${browser}`]) {
        if (DetectRTC.browser.version > version) {
          browserOk = true;
          browserValidated = true;
          console.log(`✅ Browser ${browser} version ${DetectRTC.browser.version} is valid`);
          break;
        } else {
          console.warn(`⚠️ Browser ${browser} version ${DetectRTC.browser.version} is too old (required: > ${version})`);
      swal("Please Update Browser or Try a Different Browser");
          browserOk = false;
          browserValidated = true;
          break;
        }
    }
  }
    setBrowserValid(browserOk);

    // Check webcam permissions using DetectRTC as source of truth
    return new Promise((resolve) => {
  DetectRTC.load(() => {
    const webcam = DetectRTC.isWebsiteHasWebcamPermissions;
        console.log('📹 Webcam permission check (DetectRTC - source of truth):', webcam);
        
        // Update state based on DetectRTC (this is what we show in UI)
        setWebcamAvailable(webcam);
        setWebcamPermission(webcam);
        
        if (webcam) {
          // Webcam is available - hide "Activate Webcam" button, show Next button
          webcamOk = true;
          setShowWebcamButton(false);
          const finalAllowed = networkOk && browserOk && webcamOk && viewportOk;
          console.log('🔍 Final validation (Webcam Available):', {
            networkOk,
            browserOk,
            webcamOk,
            viewportOk,
            finalAllowed
          });
          setButtonViewDisabled(!finalAllowed);
          resolve(finalAllowed);
        } else {
          // Webcam is NOT available - show "Activate Webcam" button, disable Next button
          webcamOk = false;
          setShowWebcamButton(true);
          const finalAllowed = networkOk && browserOk && webcamOk && viewportOk;
          console.log('🔍 Final validation (Webcam NOT Available):', {
            networkOk,
            browserOk,
            webcamOk,
            finalAllowed,
            showWebcamButton: true
          });
          setButtonViewDisabled(true); // Always disable Next button if webcam not available
          resolve(finalAllowed);
        }
      });
    });
  }, []);

  // Run validation on component mount and when returning from validate page
  useEffect(() => {
    const runValidation = async () => {
      // Check internet connection
      await checkInternetConnection();
      // Then validate all requirements
      await validateSystemRequirements();
    };
    
    runValidation();
  }, [validateSystemRequirements]);

  // Re-validate when component becomes visible (user returns from validate page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // User returned to the page, re-validate
        console.log('🔄 Page became visible, re-validating system requirements...');
        validateSystemRequirements();
    }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [validateSystemRequirements]);

  // Re-validate as soon as a side panel is opened or closed, so the candidate sees the check
  // clear the moment they fix it instead of having to reload the page.
  //
  // Panels get closed in every imaginable way: removed from the DOM, hidden with a style
  // change, slid off screen, or by resizing the window. Only the last of those fires an event
  // we can listen for, so poll the (cheap) measurement and re-run the full validation whenever
  // the answer actually changes.
  useEffect(() => {
    const measure = () => getViewportGap() < SIDE_PANEL_MIN_SIZE && findPageOverlays().length === 0;

    let lastClear = measure();
    let recheckTimeout = null;

    const check = () => {
      const clear = measure();
      if (clear === lastClear) return;
      lastClear = clear;
      clearTimeout(recheckTimeout);
      recheckTimeout = setTimeout(() => {
        validateSystemRequirements();
      }, 300); // Let any closing animation finish before the full re-validation
    };

    const pollInterval = setInterval(check, 1000);
    window.addEventListener('resize', check);
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });

    return () => {
      clearInterval(pollInterval);
      clearTimeout(recheckTimeout);
      window.removeEventListener('resize', check);
      observer.disconnect();
    };
  }, [validateSystemRequirements]);

  function handleClick() {
    // Store a flag that we're going to validate page
    sessionStorage.setItem("validatingWebcam", "true");
    history.push("/validate");
  }

  // Re-validate when component mounts (especially when returning from validate page)
  useEffect(() => {
    // Check if we just returned from validate page
    const wasValidating = sessionStorage.getItem("validatingWebcam");
    if (wasValidating === "true") {
      console.log('🔄 Returning from validate page, re-checking webcam permissions...');
      sessionStorage.removeItem("validatingWebcam");
      // Force DetectRTC to reload and re-check permissions
      setTimeout(() => {
        validateSystemRequirements();
      }, 500); // Small delay to ensure DetectRTC is ready
    }
  }, [validateSystemRequirements]);

  function openFullscreen() {
    // Check if we're in load testing mode
    const isLoadTesting = (typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_LOAD_TESTING === '1');
    
    if (isLoadTesting) {
      // Skip fullscreen for load testing
      history.push("/instructions");
      return;
    }
    
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
      history.push("/instructions");
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
      history.push("/instructions");
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
      history.push("/instructions");
    }
  }

  return (
    <div className="App">
      <img src={logo} alt="logo" className="App-logo" />
      <div className="main">
        <p className="sign" align="center" id="systemCompatibilityCheckTxt">System Compatibility Check</p>
        <table align="center" style={{marginBottom : '50px'}}>
          <tbody>
            <tr>
              <td className="text-center">
                <div>
                  <img src={icon} id="classIcon" alt="Icon" />
                </div>
              </td>
              <td>
                <ul>
                  <li className="test">
                    <span><b>OS:</b> {" " + JSON.stringify(DetectRTC.osName, null, 2).slice(1, -1) + " " + JSON.stringify(DetectRTC.osVersion, null, 0).slice(1, -1)}</span>
                  </li>
                  <li className="test">
                    <span><b>Browser:</b> {" " + JSON.stringify(DetectRTC.browser.name).slice(1, -1) + " " + JSON.stringify(DetectRTC.browser.version)}</span>
                  </li>
                  <li className="test">
                    <span><b>Internet Connection:</b> {" " + (sessionStorage.getItem("netspeed") === "connected" || sessionStorage.getItem("netspeed") === "online" ? "✅ Connected" : "❌ Disconnected")}</span>
                  </li>
                  <li className="test">
                    <span><b>Webcam:</b> {" " + (webcamAvailable ? "✅ Available" : "❌ Not Available")}</span>
                  </li>
                  <li className="test">
                    <span><b>Screen:</b> {" " + (viewportClear ? "✅ No side panel" : "❌ Side panel detected")}</span>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Show "Activate Webcam" button only if webcam is NOT available */}
        {showWebcamButton && (
        <center>
          <Button
            id="activateWebCamBtn"
            className="activateButton"
              variant="contained"
            onClick={handleClick}>
            Activate Your WebCam and Network Check
          </Button>
          <br />
          <br />
        </center>
        )}

        {/* One short line telling the candidate what to do - the checklist above already
            says what is wrong, and the exact measurements go to the console, not the screen */}
        {!viewportClear && (
          <div style={{
            margin: '-30px auto 24px',
            maxWidth: '90%',
            color: '#b02a37',
            fontSize: '15px',
            lineHeight: 1.5,
            textAlign: 'center'
          }}>
            {viewportOverlays.length > 0
              ? 'A browser extension is showing a panel over this page. Please close it to continue.'
              : 'Please close the browser side panel, or reset page zoom to 100%, to continue.'}
          </div>
        )}

        {/* Show Next button - disabled if webcam not available or other checks fail */}
        <center>
          <Button
            id="nextBtn"
            className="nextButton"
            size="large"
            disabled={(typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_LOAD_TESTING == 1) ? false : buttonViewDisabled}
            variant='contained'
            onClick={openFullscreen}
            title={buttonViewDisabled ? (showWebcamButton ? 'Please activate your webcam first by clicking "Activate Your WebCam and Network Check"' : 'Please complete all system checks:\n- Active Internet Connection\n- Valid Browser Version\n- Webcam Permission Granted\n- No browser side panel open') : 'Click to proceed to instructions'}>
            Next
          </Button>
          {buttonViewDisabled && !showWebcamButton && viewportClear && (
            <div style={{ 
              marginTop: '10px', 
              fontSize: '12px', 
              color: '#d32f2f',
              textAlign: 'center',
              maxWidth: '400px'
            }}>
              ⚠️ Please ensure all checks pass: Active Internet Connection, Valid Browser, and Webcam Permission
            </div>
          )}
          {showWebcamButton && (
            <div style={{ 
              marginTop: '10px', 
              fontSize: '12px', 
              color: '#ff9800',
              textAlign: 'center',
              maxWidth: '400px'
            }}>
              ℹ️ Please click "Activate Your WebCam and Network Check" to enable your webcam first
            </div>
          )}
        </center>
      </div>
    </div>
  );
}

export default SystemCheck;
